
import { useState } from 'react';

interface PaymentOptions {
  amount: number;
  currency: string;
  description: string;
  planId?: string;
  planType?: string;
  isSubscription?: boolean;
  billingPeriod?: 'monthly' | 'quarterly' | 'yearly';
  metadata?: Record<string, string>;
  customerId?: string;
}

interface PaymentResult {
  success: boolean;
  paymentId?: string;
  error?: string;
  transactionId?: string;
  amount?: number;
  customerId?: string;
  subscriptionId?: string;
}

export const useStripePayment = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastPaymentResult, setLastPaymentResult] = useState<PaymentResult | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const processPayment = async (paymentMethodId: string, options: PaymentOptions): Promise<PaymentResult> => {
    setIsProcessing(true);
    setPaymentError(null);
    
    try {
      // In a real app, this would be an API call to your backend to process the payment with Stripe
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API delay
      
      // Mock successful payment result
      const result: PaymentResult = {
        success: true,
        paymentId: 'pi_' + Math.random().toString(36).substring(2, 10),
        transactionId: 'tx_' + Math.random().toString(36).substring(2, 10),
        amount: options.amount,
        customerId: options.customerId || 'cus_' + Math.random().toString(36).substring(2, 10),
        subscriptionId: options.isSubscription ? 'sub_' + Math.random().toString(36).substring(2, 10) : undefined
      };
      
      setLastPaymentResult(result);
      return result;
    } catch (error) {
      console.error('Payment processing error:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown payment error';
      setPaymentError(errorMessage);
      
      const errorResult: PaymentResult = {
        success: false,
        error: errorMessage
      };
      
      setLastPaymentResult(errorResult);
      return errorResult;
    } finally {
      setIsProcessing(false);
    }
  };

  // Add redirectToCheckout function to match what's being used in components
  const redirectToCheckout = async (options: PaymentOptions): Promise<{ success: boolean; error?: string }> => {
    // In a real app, this would redirect to Stripe Checkout
    console.log('Redirecting to Stripe Checkout with options:', options);
    
    // Mock implementation - would normally create a Checkout Session and redirect
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // For now just simulate a successful redirect
    return { success: true };
  };

  return {
    isProcessing,
    lastPaymentResult,
    paymentError,
    processPayment,
    redirectToCheckout,
    // For StripePaymentProcessor component compatibility
    lastTransaction: lastPaymentResult && lastPaymentResult.success ? {
      id: lastPaymentResult.transactionId || '',
      status: 'success',
      amount: lastPaymentResult.amount || 0,
      customerId: lastPaymentResult.customerId,
      subscriptionId: lastPaymentResult.subscriptionId
    } : null
  };
};
